import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { AuthenticationService } from '../services/authentication.service';
import { ProjectColumnService } from '../services/project-column.service';
import { ResourceService } from '../services/resource.service';
import { IProjectColumn } from '../shared/models/project-column';
import { Resource } from '../shared/models/resource';
import { ToastService } from '../shared/toast/toast.service';
import { DeleteActionComponent } from './delete-action.component';

@Component({
  selector: 'pps-manage-project-columns',
  templateUrl: './manage-project-columns.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        min-height: 0;
      }

      .manage-project-columns-page {
        height: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .manage-project-columns-content {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      .manage-project-columns-hint {
        flex: 0 0 auto;
        height: 30px;
        line-height: 30px;
      }

      .manage-project-columns-grid {
        flex: 1 1 auto;
        min-height: 0;
      }
    `
  ],
})
export class ManageProjectColumnsComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Project Columns", icon: "pi-fw fas fa-columns"},
  ];
  
  projectColumns: IProjectColumn[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => `${param.data.id}`,
    defaultColDef:{
      enableCellChangeFlash: true
    }
  };

  constructor(private _projectColumnService: ProjectColumnService,
              private _confirm: ConfirmationService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
  }  

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.projectColumns.length = 0;
    this.subs.sink = this._projectColumnService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.projectColumns = apiResponse.result;
          this._toast.succeeded(`${this.projectColumns.length} columns loaded`);
        }else{
          this._toast.failed(`Failed to load project columns: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: 'Display Order',
        field:'sortNo',
        colId: 'sortNo',
        width: 120,
        editable: true,
        sortable: true,
        onCellValueChanged: this.projectColumnChanged,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Project Column',
        field:'columnName',
        filter: false,
        width: 300,
        editable: false
      },
      {
        headerName: 'Description',
        field:'description',
        filter: false,
        flex: 1,
        editable: false
      }
    ];

    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }

    return cols;
  }

  private projectColumnChanged(params){
    let _this = params.context.componentParent;
    let projectColumn = params.data;
    let shouldReload = params.colDef.field === "sortNo" && +params.newValue !== +params.oldValue;

    if (params.colDef.field === "orderNo" && isNaN(projectColumn.orderNo)){
      projectColumn.orderNo = 0;
    }

    _this._spinner.show(_this.spinnerName);
    projectColumn.orderNo = +projectColumn.orderNo;
    _this.subs.sink = _this._projectColumnService.update(projectColumn)
      .subscribe(res => {
        if (res.succeed){
          _this._toast.succeeded(`Sort No updated successfully`);
          if (shouldReload){
           _this.loadData();
          }
        }else{
          _this._toast.failed(`Update project column failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => _this._spinner.hide(_this.spinnerName));
  }
}
